import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { NOTICE_KINDS, Notice, NoticeKind } from "@equipped/core/backend/client";
import { useAuth } from "../auth/AuthContext";
import { userMatchesQuery } from "../lib/userSearch";
import { MainCard } from "../components/MainCard";
import { ConfirmDialog } from "../components/ConfirmDialog";

/**
 * Notices tab, re-skinned to MUI/Mantis (docs/337-ADMIN-MANTIS-MIGRATION.md v2) — mirrors
 * `apps/admin-web/src/pages/NoticesPage.tsx` (itself mirroring `apps/admin/app/(tabs)/notices.tsx` /
 * `specs/admin/screens/notices.md`): compose + send a notice to one account, an explicit set, or
 * everyone (confirm-gated via the shared `ConfirmDialog` instead of `window.confirm`), plus a list of
 * every notice sent. Every `useQuery`/`useMutation` here is lifted UNCHANGED — only the returned JSX
 * differs.
 */
export function NoticesPage() {
  const { client, token } = useAuth();
  const queryClient = useQueryClient();

  const [kind, setKind] = useState<NoticeKind>("info");
  const [body, setBody] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [broadcast, setBroadcast] = useState(false);
  const [sentSummary, setSentSummary] = useState<string | null>(null);
  const [confirmBroadcast, setConfirmBroadcast] = useState(false);

  const noticesQuery = useQuery({
    queryKey: ["admin-notices"],
    queryFn: () => client!.adminNotices(token!),
    enabled: !!client && !!token,
  });
  const usersQuery = useQuery({
    queryKey: ["admin-users", false],
    queryFn: () => client!.adminUsers(token!),
    enabled: !!client && !!token,
  });
  const notices = noticesQuery.data ?? [];
  const users = usersQuery.data ?? [];

  const emailById = useMemo(() => new Map(users.map((u) => [u.id, u.email])), [users]);
  const filteredUsers = useMemo(() => users.filter((u) => userMatchesQuery(u, query)), [users, query]);

  const toggleTarget = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const sendMutation = useMutation({
    mutationFn: () =>
      broadcast
        ? client!.adminSendNotice(token!, { broadcast: true, kind, body: body.trim() })
        : client!.adminSendNotice(token!, { userIds: [...selected], kind, body: body.trim() }),
    onSuccess: (res) => {
      queryClient.setQueryData<Notice[]>(["admin-notices"], (list) =>
        [...res.notices, ...(list ?? [])].sort((a, b) => b.createdAt - a.createdAt),
      );
      setBody("");
      setSelected(new Set());
      setBroadcast(false);
      setSentSummary(`Sent to ${res.count} recipient(s).`);
    },
  });

  const canSend = body.trim().length > 0 && (broadcast || selected.size > 0);

  const send = () => {
    if (!canSend) return;
    if (broadcast) {
      setConfirmBroadcast(true);
      return;
    }
    setSentSummary(null);
    sendMutation.mutate();
  };

  return (
    <>
      <MainCard title="Send a notice" sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {NOTICE_KINDS.map((k) => (
            <Button
              key={k}
              size="small"
              variant={kind === k ? "contained" : "outlined"}
              color={kind === k ? "primary" : "secondary"}
              onClick={() => setKind(k)}
            >
              {k}
            </Button>
          ))}
        </Box>
        <TextField
          multiline
          minRows={3}
          fullWidth
          placeholder="Notice message…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={<Switch checked={broadcast} onChange={(e) => setBroadcast(e.target.checked)} />}
          label="Send to everyone"
          sx={{ mb: 2, display: "block" }}
        />
        {broadcast ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will send to every account. There&rsquo;s no undo.
          </Alert>
        ) : (
          <>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by name or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1, maxHeight: 220, overflowY: "auto" }}>
              {filteredUsers.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                  No matching accounts.
                </Typography>
              ) : (
                filteredUsers.map((u) => (
                  <Chip
                    key={u.id}
                    label={u.email}
                    color={selected.has(u.id) ? "primary" : "default"}
                    onClick={() => toggleTarget(u.id)}
                    sx={{ cursor: "pointer" }}
                  />
                ))
              )}
            </Box>
            {selected.size > 0 ? (
              <Typography variant="body2" color="text.secondary">
                {selected.size} selected
              </Typography>
            ) : null}
          </>
        )}
        {sendMutation.isError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(sendMutation.error as Error).message}
          </Alert>
        ) : null}
        <Button variant="contained" sx={{ mt: 2 }} disabled={!canSend || sendMutation.isPending} onClick={send}>
          {sendMutation.isPending ? <CircularProgress size={18} color="inherit" /> : "Send notice"}
        </Button>
        {sentSummary ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {sentSummary}
          </Typography>
        ) : null}
      </MainCard>

      <MainCard title="Sent notices">
        {noticesQuery.isError ? <Alert severity="error">{(noticesQuery.error as Error).message}</Alert> : null}
        {noticesQuery.isLoading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : notices.length === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 0 }}>
            No notices sent yet.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {notices.map((n) => (
              <Box key={n.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                <Box>
                  <Typography sx={{ mb: 0.5 }}>{n.body}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {emailById.get(n.userId) ?? n.userId} · {n.kind} · {new Date(n.createdAt * 1000).toLocaleString()}
                  </Typography>
                </Box>
                <Chip label={n.readAt ? "Read" : "Unread"} color={n.readAt ? "success" : "default"} size="small" />
              </Box>
            ))}
          </Stack>
        )}
      </MainCard>

      <ConfirmDialog
        open={confirmBroadcast}
        title="Send to everyone?"
        body="This will send this notice to EVERY account. This cannot be undone."
        confirmLabel="Send to everyone"
        busy={sendMutation.isPending}
        error={sendMutation.isError ? (sendMutation.error as Error).message : null}
        onCancel={() => setConfirmBroadcast(false)}
        onConfirm={() => {
          setConfirmBroadcast(false);
          setSentSummary(null);
          sendMutation.mutate();
        }}
      />
    </>
  );
}
